import { ItemType, itemTypeFor } from 'admin-panel/utils/items';

import type ItemModel from 'admin-panel/models/item';
import type IntegrationsRemoteReferenceModel from 'admin-panel/models/integrations/remote-reference';
import type PriceModel from 'admin-panel/models/price';

export enum ItemSortKey {
  PageOrder = 'page-order',
  Type = 'type',
  TitleTeaser = 'title-teaser',
  Price = 'price',
  RemoteReference = 'remote-reference',
  Link = 'link',
  Image = 'image',
  Visibility = 'visibility',
}

export const ITEM_SORT_KEYS = Object.values(ItemSortKey);

export const ITEM_SORT_KEYS_NEEDING_DATA = [
  ItemSortKey.Price,
  ItemSortKey.RemoteReference,
];

export interface ItemSortContext {
  isShopPage: boolean;
  currencyId?: string;
  locale?: string;
}

const TYPE_ORDER = [
  ItemType.Information,
  ItemType.Request,
  ItemType.Reservation,
  ItemType.Special,
  ItemType.Shop,
];

const LAST = Number.POSITIVE_INFINITY;

const flag = (hasValue: boolean) => (hasValue ? 0 : 1);

function priceOf(item: ItemModel, currencyId: string | undefined) {
  const prices = (item.hasMany('prices').value()?.toArray() ??
    []) as PriceModel[];
  const price = prices.find(
    (record) =>
      !record.isDestroyed && record.belongsTo('currency').id() === currencyId
  );
  const value = Number(price?.value);

  return price && Number.isFinite(value) ? value : LAST;
}

function remoteReferenceNameOf(item: ItemModel) {
  const references = (item
    .hasMany('integrationsRemoteReferences')
    .value()
    ?.toArray() ?? []) as IntegrationsRemoteReferenceModel[];

  return references[0]?.name ?? null;
}

function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  locale: string | undefined
) {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return a.localeCompare(b, locale, { sensitivity: 'base', numeric: true });
}

function comparatorFor(
  key: ItemSortKey,
  context: ItemSortContext
): ((a: ItemModel, b: ItemModel) => number) | null {
  switch (key) {
    case ItemSortKey.Type: {
      const rank = (item: ItemModel) =>
        TYPE_ORDER.indexOf(itemTypeFor(item.workflow, context.isShopPage));

      return (a, b) => rank(a) - rank(b);
    }
    case ItemSortKey.TitleTeaser:
      return (a, b) =>
        compareText(a.title, b.title, context.locale) ||
        compareText(a.teaser, b.teaser, context.locale);
    case ItemSortKey.Price:
      return (a, b) => {
        const priceA = priceOf(a, context.currencyId);
        const priceB = priceOf(b, context.currencyId);

        if (priceA === priceB) return 0;

        return priceA < priceB ? -1 : 1;
      };
    case ItemSortKey.RemoteReference:
      return (a, b) =>
        compareText(
          remoteReferenceNameOf(a),
          remoteReferenceNameOf(b),
          context.locale
        );
    case ItemSortKey.Link:
      return (a, b) => flag(Boolean(a.linkUrl)) - flag(Boolean(b.linkUrl));
    case ItemSortKey.Image:
      return (a, b) =>
        flag(Boolean(a.belongsTo('image').id())) -
        flag(Boolean(b.belongsTo('image').id()));
    case ItemSortKey.Visibility:
      return (a, b) => flag(a.visible !== false) - flag(b.visible !== false);
    default:
      return null;
  }
}

export function sortItems(
  items: ItemModel[],
  key: ItemSortKey,
  context: ItemSortContext
) {
  const compare = comparatorFor(key, context);

  return compare ? [...items].sort(compare) : items;
}
